import { useMemo } from 'react';
import { ComboBox, ComboBoxProps, Layer } from '@carbon/react';
import { useAtom, useSetAtom } from 'jotai';
import { debounce } from 'lodash-es';

import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import type { WellsAutocompleteRow } from 'types/api-responses';

interface Props extends Omit<
  ComboBoxProps<WellsAutocompleteRow>,
  'items' | 'itemToString' | 'selectedItem' | 'onChange'
> {
  selectedItem: WellsAutocompleteRow | null;
  onChange: (selectedItem: WellsAutocompleteRow | null) => void;
  /** Wraps the ComboBox in a Carbon `<Layer>` for white-on-gray surfaces. */
  light?: boolean;
}

const QUERY_HREF = { href: '/queries/wells/autocomplete' };
// Uses the {href} object form rather than a named ApiQueryResource — targets
// a hardcoded path not registered in the Rails routes config for query.
// If this endpoint path changes in the BE, update here manually.
const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 2;

const itemToString = (row: WellsAutocompleteRow | null): string =>
  row?.uwi ?? '';

/**
 * Searchable UWI autocomplete backed by `POST /queries/wells/autocomplete`.
 * Each instance owns its own `searchAtomPair` so concurrent autocompletes
 * (e.g. multiple modals) don't cross-cancel each other's requests.
 *
 * Mirrors {@link import('../survey-area-autocomplete').SurveyAreaAutocomplete}
 * — pass `selectedItem` + `onChange` to track selection, everything else
 * (id, titleText, placeholder, disabled) forwards to Carbon's ComboBox.
 *
 * @return {JSX.Element} Carbon ComboBox bound to the wells autocomplete query
 */
export const WellsAutocomplete = ({
  selectedItem,
  onChange,
  light = false,
  ...rest
}: Props): JSX.Element => {
  const { searchAtom, resetSearchDataAtom } = useMemo(
    () => searchAtomPair<WellsAutocompleteRow, SearchParams>(QUERY_HREF),
    []
  );

  const [query, setPayload] = useAtom(searchAtom);
  const resetSearchData = useSetAtom(resetSearchDataAtom);

  const debouncedSetPayload = useMemo(
    () => debounce(setPayload, SEARCH_DEBOUNCE_MS),
    [setPayload]
  );

  const comboBox = (
    <ComboBox<WellsAutocompleteRow>
      items={query?.data?.results ?? []}
      itemToString={itemToString}
      shouldFilterItem={() => true}
      itemToElement={(row) => (
        <span>
          {row.uwi}
          {row.name ? ` — ${row.name}` : ''}
        </span>
      )}
      selectedItem={selectedItem}
      onChange={({ selectedItem: next }) => {
        onChange(next ?? null);
      }}
      onInputChange={(text) => {
        const trimmed = (text ?? '').trim();
        if (trimmed.length < MIN_SEARCH_LENGTH) {
          // Reset before the debounce window so a stale prior result
          // doesn't flash in the dropdown after the user clears.
          resetSearchData(undefined);
          return;
        }
        // Intentional: prior results stay visible until the debounce fires,
        // unlike SurveyAreaAutocomplete which clears on every keystroke.
        // Reduces flicker when typing past MIN_SEARCH_LENGTH.
        debouncedSetPayload((c) => ({
          ...c,
          text: trimmed,
          pageSize: 10,
        }));
      }}
      {...rest}
    />
  );

  return light ? <Layer>{comboBox}</Layer> : comboBox;
};
